import React, { useEffect, useState } from "react";
import RootRoutes from "./routes/RootRoute";
import publicRoutes from "./routes/PublicRoutes";
import customerRoutes from "./routes/Customer/CustomerRoutes";
import AdminRoutes from "./routes/Admin/AdminRoutes";
import LoginModal from "./components/customer/LoginModal";

const allRoutes = [...publicRoutes, ...AdminRoutes, ...customerRoutes];

declare global {
  interface Window {
    googleTranslateElementInit?: () => void;
  }
}

const App: React.FC = () => {
  const [showLoginModal, setShowLoginModal] = useState(false);

  /**
   * Initialize Google Translate
   *
   * Google Translate creates its dropdown asynchronously.
   * We update the language names after it has created the dropdown.
   *
   * IMPORTANT:
   * Do NOT use MutationObserver on document.body here.
   * It can create a continuous DOM-change loop and freeze the page.
   */
  useEffect(() => {
    let timeoutIds: ReturnType<typeof setTimeout>[] = [];

    const forceEnglishLanguageNames = () => {
      const selectElement = document.querySelector(
        ".goog-te-combo"
      ) as HTMLSelectElement | null;

      if (!selectElement) {
        return;
      }

      const languageNames = new Intl.DisplayNames(["en"], {
        type: "language",
      });

      Array.from(selectElement.options).forEach((option, index) => {
        // Default option
        if (index === 0) {
          if (option.text !== "Select Language") {
            option.text = "Select Language";
          }
          return;
        }

        const code = option.value?.trim();

        if (!code) {
          return;
        }

        try {
          const englishName = languageNames.of(code);

          if (englishName && option.text !== englishName) {
            option.text = englishName;
          }
        } catch {
          // Keep Google's original language name
        }
      });
    };

    window.googleTranslateElementInit = () => {
      const translateElement = document.getElementById(
        "google_translate_element"
      );

      if (!translateElement) {
        return;
      }

      if (
        typeof google !== "undefined" &&
        google.translate &&
        google.translate.TranslateElement
      ) {
        // Prevent duplicate Google Translate initialization
        if (translateElement.children.length === 0) {
          new google.translate.TranslateElement(
            {
              pageLanguage: "en",
              autoDisplay: false,
            },
            "google_translate_element"
          );
        }

        /*
         * Google creates .goog-te-combo asynchronously.
         * Try a few times instead of observing the entire body.
         */
        timeoutIds = [
          setTimeout(forceEnglishLanguageNames, 500),
          setTimeout(forceEnglishLanguageNames, 1200),
          setTimeout(forceEnglishLanguageNames, 2000),
        ];
      }
    };

    /**
     * Load Google Translate script
     */
    const existingScript = document.getElementById(
      "google-translate-script"
    );

    if (!existingScript) {
      const script = document.createElement("script");

      script.id = "google-translate-script";
      script.src =
        "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;

      document.body.appendChild(script);
    } else if (
      typeof google !== "undefined" &&
      google.translate &&
      google.translate.TranslateElement
    ) {
      window.googleTranslateElementInit();
    }

    /**
     * Cleanup
     */
    return () => {
      timeoutIds.forEach((id) => clearTimeout(id));

      window.googleTranslateElementInit = undefined;
    };
  }, []);

  /**
   * Session expiration handlers
   */
  useEffect(() => {
    const handleCustomerSessionExpired = () => {
      setShowLoginModal(true);
    };

    const handleAdminSessionExpired = () => {
      if (window.location.pathname !== "/admin/login") {
        window.location.replace("/admin/login");
      }
    };

    window.addEventListener(
      "customer-session-expired",
      handleCustomerSessionExpired
    );

    window.addEventListener(
      "admin-session-expired",
      handleAdminSessionExpired
    );

    return () => {
      window.removeEventListener(
        "customer-session-expired",
        handleCustomerSessionExpired
      );

      window.removeEventListener(
        "admin-session-expired",
        handleAdminSessionExpired
      );
    };
  }, []);

  return (
    <>
      <RootRoutes allRoutes={allRoutes} />

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
    </>
  );
};

export default App;